import { useState } from "react";
import {
  useTenantIncidents,
  useUpdateIncidentStatus,
  type IncidentFilters,
  type TenantIncident,
} from "@/hooks/useTenantIncidents";
import { IncidentCard } from "./IncidentCard";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, AlertTriangle, Filter } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = ["all", "open", "acknowledged", "resolved", "suppressed"] as const;
const SEVERITY_OPTIONS = ["all", "critical", "high", "medium", "low"] as const;
const CATEGORY_OPTIONS = ["all", "onboarding", "billing", "support", "compliance", "ops"] as const;

interface IncidentInboxProps {
  onInvestigate?: (incident: TenantIncident) => void;
  companyId?: string;
  maxItems?: number;
}

export function IncidentInbox({ onInvestigate, companyId, maxItems }: IncidentInboxProps) {
  const [filters, setFilters] = useState<IncidentFilters>({
    status: "open",
    severity: "all",
    category: "all",
    companyId,
  });
  const [search, setSearch] = useState("");

  const { data: incidents = [], isLoading } = useTenantIncidents(filters);
  const updateStatus = useUpdateIncidentStatus();

  const filtered = search
    ? incidents.filter(
        (i) =>
          i.title.toLowerCase().includes(search.toLowerCase()) ||
          i.tenant_name?.toLowerCase().includes(search.toLowerCase()) ||
          i.summary.toLowerCase().includes(search.toLowerCase())
      )
    : incidents;

  const displayed = maxItems ? filtered.slice(0, maxItems) : filtered;

  const handleAcknowledge = async (incident: TenantIncident) => {
    try {
      await updateStatus.mutateAsync({ incidentId: incident.id, status: "acknowledged" });
      toast.success("Incident acknowledged");
    } catch {
      toast.error("Failed to acknowledge incident");
    }
  };

  const handleResolve = async (incident: TenantIncident) => {
    try {
      await updateStatus.mutateAsync({ incidentId: incident.id, status: "resolved" });
      toast.success("Incident resolved");
    } catch {
      toast.error("Failed to resolve incident");
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative w-full sm:w-56">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search incidents…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex flex-wrap gap-1.5">
          <Filter className="h-4 w-4 text-muted-foreground mt-1" />
          {STATUS_OPTIONS.map((s) => (
            <Badge
              key={s}
              variant={filters.status === s ? "default" : "outline"}
              className="cursor-pointer capitalize text-xs"
              onClick={() => setFilters((f) => ({ ...f, status: s as any }))}
            >
              {s}
            </Badge>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <span className="text-xs text-muted-foreground mr-1 mt-0.5">Severity:</span>
        {SEVERITY_OPTIONS.map((s) => (
          <Badge
            key={s}
            variant={filters.severity === s ? "default" : "outline"}
            className="cursor-pointer capitalize text-xs"
            onClick={() => setFilters((f) => ({ ...f, severity: s as any }))}
          >
            {s}
          </Badge>
        ))}
        <span className="text-xs text-muted-foreground mx-2 mt-0.5">Category:</span>
        {CATEGORY_OPTIONS.map((c) => (
          <Badge
            key={c}
            variant={filters.category === c ? "default" : "outline"}
            className="cursor-pointer capitalize text-xs"
            onClick={() => setFilters((f) => ({ ...f, category: c as any }))}
          >
            {c}
          </Badge>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28 w-full rounded-lg" />
          ))}
        </div>
      ) : displayed.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <AlertTriangle className="h-8 w-8 mb-2 opacity-40" />
          <p className="text-sm">No incidents match the current filters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayed.map((incident) => (
            <IncidentCard
              key={incident.id}
              incident={incident}
              onInvestigate={onInvestigate}
              onAcknowledge={handleAcknowledge}
              onResolve={handleResolve}
            />
          ))}
          {maxItems && filtered.length > maxItems && (
            <p className="text-xs text-muted-foreground text-center">
              +{filtered.length - maxItems} more incidents
            </p>
          )}
        </div>
      )}
    </div>
  );
}
